/**
 * GenerationPreview — fullscreen preview overlay for the generation grid.
 * Uses Motion for fly-in/out animation from the source grid cell.
 * Contains Swiper (Virtual) for horizontal shot navigation.
 */

import { useState, useRef, useCallback, type MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Virtual } from 'swiper/modules';
import type SwiperType from 'swiper';
import 'swiper/css';
import 'swiper/css/virtual';
import { SparkleIcon } from '@/components/icons/SparkleIcon.tsx';
import type { CatalogModel, CostEstimate } from '@/types/ai.ts';
import styles from './GenerationPreview.module.css';

/** Structural type for items — accepts progressItems from useAiWorkspaceFlow */
interface PreviewItem {
  id: string;
  completed: boolean;
  sourceThumb?: string;
  label?: string;
  shotId?: number;
}

interface Props {
  previewShotId: number;
  progressItems: PreviewItem[];
  completedMedia: Map<string, string>;
  isEnhance: boolean;
  sourceRect: DOMRect | null;
  cellRefsRef: MutableRefObject<Map<string, HTMLDivElement>>;
  catalog: CatalogModel[];
  activeModelId: string;
  costEstimate: CostEstimate;
  onPreviewShot: (shotId: number | null) => void;
  onRetry: (shotId: number) => void;
  onPrioritize: (shotId: number) => void;
  onTouchUpFromPreview: (shotId: number, prompt: string) => void;
}

function computeFlyValues(rect: DOMRect | null): { x: number; y: number; scale: number } {
  if (!rect) return { x: 0, y: 0, scale: 1 };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const x = rect.left + rect.width / 2 - vw / 2;
  const y = rect.top + rect.height / 2 - vh / 2;
  const scale = Math.min(rect.width / vw, rect.height / vh);
  return { x, y, scale };
}

const SPRING = { type: 'spring' as const, stiffness: 300, damping: 30 };
const INSTANT = { duration: 0 };

export function GenerationPreview({
  previewShotId, progressItems, completedMedia, isEnhance,
  sourceRect, cellRefsRef, catalog, activeModelId, costEstimate,
  onPreviewShot, onRetry, onPrioritize, onTouchUpFromPreview,
}: Props) {
  const { t } = useTranslation('aiWorkspace');
  const reducedMotion = useReducedMotion();
  const swiperRef = useRef<SwiperType | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const exitRectRef = useRef<DOMRect | null>(sourceRect);

  const [compare, setCompare] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [touchUpOpen, setTouchUpOpen] = useState(false);
  const [touchUpPrompt, setTouchUpPrompt] = useState('');

  const previewIdx = progressItems.findIndex(p => p.shotId === previewShotId);
  const previewItem = previewIdx >= 0 ? progressItems[previewIdx] : null;
  if (!previewItem) return null;

  const isCompleted = previewItem.completed;
  const mediaUrl = isCompleted ? completedMedia.get(previewItem.id) : null;
  const originalUrl = previewItem.sourceThumb ?? null;
  const selectedModel = catalog.find(m => m.id === activeModelId);
  const perShotCost = selectedModel?.cost ?? costEstimate.perShot;
  const perShotSeconds = selectedModel?.estSeconds ?? 15;
  const showOriginal = compare && originalUrl;

  const flyInitial = reducedMotion ? { x: 0, y: 0, scale: 1 } : computeFlyValues(sourceRect);

  const getExitValues = () => {
    const cell = cellRefsRef.current.get(previewItem.id);
    if (cell) exitRectRef.current = cell.getBoundingClientRect();
    return reducedMotion ? { x: 0, y: 0, scale: 1 } : computeFlyValues(exitRectRef.current);
  };

  const dismiss = useCallback(() => {
    setTouchUpOpen(false);
    onPreviewShot(null);
  }, [onPreviewShot]);

  return createPortal(
    <motion.div
      className={styles.overlay}
      initial={{ opacity: reducedMotion ? 1 : 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.6, ease: 'easeIn' } }}
      transition={{ duration: 0.3 }}
      onClick={dismiss}
    >
      <motion.div exit={{ opacity: 0, transition: INSTANT }}>
        <div className={styles.previewTopRight}>
          {originalUrl && (
            <button
              className={`${styles.previewCloseBtn} ${compare ? styles.previewCompareBtnActive : ''}`}
              onClick={e => { e.stopPropagation(); setCompare(c => !c); }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8v16z" />
              </svg>
            </button>
          )}
          <button className={styles.previewCloseBtn} onClick={dismiss}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="4,14 4,20 10,20" />
              <polyline points="20,10 20,4 14,4" />
              <line x1="14" y1="10" x2="21" y2="3" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
          </button>
        </div>
      </motion.div>

      {!isEnhance && mediaUrl && !showOriginal && (
        <motion.div exit={{ opacity: 0, transition: INSTANT }}>
          <div className={styles.previewBottomLeft}>
            <button
              className={styles.previewCloseBtn}
              onClick={e => {
                e.stopPropagation();
                const vid = videoRef.current;
                if (!vid) return;
                if (vid.paused) { vid.play(); setPlaying(true); }
                else { vid.pause(); setPlaying(false); }
              }}
            >
              {playing ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="6,4 20,12 6,20" />
                </svg>
              )}
            </button>
          </div>
        </motion.div>
      )}

      <motion.div
        className={styles.card}
        initial={flyInitial}
        animate={{ x: 0, y: 0, scale: 1 }}
        exit={getExitValues()}
        transition={reducedMotion ? INSTANT : SPRING}
        onClick={e => e.stopPropagation()}
      >
        <Swiper
          modules={[Virtual]}
          virtual
          initialSlide={previewIdx}
          slidesPerView={1}
          spaceBetween={0}
          speed={400}
          onSwiper={swiper => { swiperRef.current = swiper; }}
          onSlideChange={swiper => {
            const item = progressItems[swiper.activeIndex];
            if (item?.shotId != null && item.shotId !== previewShotId) {
              onPreviewShot(item.shotId);
            }
          }}
          style={{ width: '100%', height: '100%' }}
        >
          {progressItems.map((item, i) => {
            const itemMedia = completedMedia.get(item.id);
            return (
              <SwiperSlide key={item.id} virtualIndex={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {showOriginal && item.sourceThumb ? (
                  <img className={styles.previewImage} src={item.sourceThumb} alt="" />
                ) : itemMedia ? (
                  isEnhance
                    ? <img className={styles.previewImage} src={itemMedia} alt="" />
                    : <video ref={item.id === previewItem.id ? videoRef : undefined} className={styles.previewImage} src={itemMedia} autoPlay loop muted playsInline />
                ) : item.sourceThumb ? (
                  <img className={styles.previewImage} src={item.sourceThumb} alt="" />
                ) : (
                  <div className={styles.previewPendingLabel}>{item.label ?? '?'}</div>
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>
      </motion.div>

      <motion.div exit={{ opacity: 0, transition: INSTANT }}>
        <div className={styles.previewActionWrap} onClick={e => e.stopPropagation()}>
          {isCompleted ? (
            <div className={styles.previewActionRow}>
              <button className={styles.previewActionBtn} onClick={() => onRetry(previewShotId)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23,4 23,10 17,10" />
                  <polyline points="1,20 1,14 7,14" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
                  <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
                </svg>
                <div className={styles.previewActionBtnContent}>
                  <span>{t('tryAgain')}</span>
                  <span className={styles.previewActionBtnMeta}>~{perShotSeconds}s  {t('creditsUnit', { count: perShotCost })}</span>
                </div>
              </button>
              <button className={styles.previewActionBtn} onClick={() => setTouchUpOpen(true)}>
                <SparkleIcon />
                <div className={styles.previewActionBtnContent}>
                  <span>{t('touchUpEnhance')}</span>
                  <span className={styles.previewActionBtnMeta}>~{perShotSeconds}s  {t('creditsUnit', { count: perShotCost })}</span>
                </div>
              </button>
            </div>
          ) : (
            <button className={styles.previewActionBtn} onClick={() => onPrioritize(previewShotId)}>
              {t('generateNext')}
            </button>
          )}
        </div>
      </motion.div>

      {touchUpOpen && (
        <div className={styles.touchUpDialog} onClick={e => e.stopPropagation()}>
          <div className={styles.touchUpDialogBody}>
            <div className={styles.touchUpDialogLeft}>
              <div className={styles.sectionLabel}>{t('touchUpInstruction')}</div>
              <textarea
                className={styles.promptTextarea}
                value={touchUpPrompt}
                onChange={e => setTouchUpPrompt(e.target.value)}
                placeholder={t('touchUpPromptPlaceholder')}
                rows={3}
                autoFocus
              />
              <div className={styles.touchUpDialogButtons}>
                <button className={styles.confirmNoBtn} onClick={() => setTouchUpOpen(false)}>
                  {t('cancel')}
                </button>
                <button
                  className={styles.previewActionBtn}
                  disabled={!touchUpPrompt.trim()}
                  onClick={() => {
                    onTouchUpFromPreview(previewShotId, touchUpPrompt);
                    setTouchUpOpen(false);
                    onPreviewShot(null);
                  }}
                >
                  <SparkleIcon />
                  <div className={styles.previewActionBtnContent}>
                    <span>{t('touchUpEnhance')}</span>
                    <span className={styles.previewActionBtnMeta}>~{perShotSeconds}s  {t('creditsUnit', { count: perShotCost })}</span>
                  </div>
                </button>
              </div>
            </div>
            {mediaUrl && <img className={styles.touchUpDialogThumb} src={mediaUrl} alt="" />}
          </div>
        </div>
      )}
    </motion.div>,
    document.body,
  );
}
